import { isEmptySlateDocument } from "../../../core/constants";
import { Configuration, RegistryDetails } from "../../../resources/modelTypes";
import { SectionComponentProps } from "../../types";
import { useWebsiteWidth } from "../../utils";
import { DisplayPhoto } from "../DisplayPhoto";
import { ThemedRichText } from "./ThemedRichText";
import { css } from "@emotion/react";
import { SmallHeadline } from "@minted/fancyclothes";
import { Button as FancyButton } from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo } from "react";

const styles = {
  column: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
    width: 100%;
  `,
  image: css`
    aspect-ratio: 1;
    height: ${styleUtils.rem(120)};
    width: ${styleUtils.rem(120)};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    min-height: ${styleUtils.rem(120)};
    min-width: ${styleUtils.rem(120)};
    max-height: ${styleUtils.rem(120)};
    max-width: ${styleUtils.rem(120)};
  `,
  marginLeft: css`
    margin-left: ${theme.spacing.x4};
  `,
  // We do not want to inherit the page text alignment properties.
  textAlignment: (isMobile: boolean) => css`
    text-align: ${isMobile ? "center" : "left"}; !important;
  `,
  wrapper: (isMobile: boolean) => css`
    align-items: center;
    display: flex;
    flex-direction: ${isMobile ? "column" : "row"};
    gap: ${theme.spacing.x4};
    justify-content: flex-start;
    padding-top: ${isMobile ? theme.spacing.x4 : theme.spacing.x8};
    padding-bottom: ${isMobile ? theme.spacing.x4 : theme.spacing.x8};
    width: 100%;
    box-sizing: border-box;
    max-width: ${styleUtils.rem(728)};
    margin-left: auto;
    margin-right: auto;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      max-width: ${styleUtils.rem(335)};
      flex-direction: column;
      padding: ${theme.spacing.x4};
    `)};
  `,
};

type DisplayRegistrySectionProps = {
  configuration: Configuration;
} & SectionComponentProps;

const DisplayRegistrySection = ({
  configuration,
  section,
}: DisplayRegistrySectionProps) => {
  const details = section.sectionDetails as RegistryDetails;

  const { ref, width } = useWebsiteWidth();

  // The 600px width is determined by minted-components and
  // in other places, we use isMobile, but we actually need the width of the
  // preview page here rather than the width of the entire page.
  const isMobile: boolean = width !== null && width < 600;

  return (
    <div css={styles.wrapper(isMobile)} ref={ref}>
      {section.photo && (
        <div css={styles.image}>
          <DisplayPhoto
            configuration={configuration}
            isRegistryPhoto
            section={section}
          />
        </div>
      )}

      <div css={styles.column}>
        {details.title && (
          <div css={styles.textAlignment(isMobile)}>
            <SmallHeadline>{details.title}</SmallHeadline>
          </div>
        )}

        {!isEmptySlateDocument(details.content) && (
          <ThemedRichText content={details.content} />
        )}
      </div>

      {details.websiteUrl && (
        // The margin left will offset the centered button on mobile
        // and we need the margin on desktop
        <div css={!isMobile && styles.marginLeft}>
          <FancyButton
            text={details.buttonText || "View Website"}
            url={details.websiteUrl}
          />
        </div>
      )}
    </div>
  );
};

const MemoizedDisplayRegistrySection = memo(DisplayRegistrySection);

export { MemoizedDisplayRegistrySection as DisplayRegistrySection };
